import { FC, useEffect, useMemo } from 'react';
import { FeaturesetValues } from '../../network';
import { MetricKeyEnum, useLayerStore } from '../LayerControls/layerStore';
import { useStandingMeshStore } from '../Standing/standingMeshStore';
import { Vector3 } from 'three';
import { ThreeDLabel } from '../3dModels/ThreeDLabel';
import { useHighlighter } from '../ui/useHighlighter';

interface SagittalBalancePlaneProps {
  featuresetValues: FeaturesetValues | undefined;
}

const layerKey = MetricKeyEnum.SagittalBalancePlane;

export const SagittalBalancePlane: FC<SagittalBalancePlaneProps> = ({
  featuresetValues,
}) => {
  useHighlighter({ layerKey });

  const { addLayerState, isLayerVisible } = useLayerStore();
  const { standingMeshBoundingBox } = useStandingMeshStore();

  const result = useMemo(() => {
    try {
      if (!featuresetValues) {
        throw new Error(
          'Featureset values are not defined in sagittal balance plane'
        );
      }

      const coronalMSLPLane = featuresetValues.mesh_metadata?.msl_plane;

      const {
        sagittal_balance_plane, // consider this as position 1
        sagittal_balance, // this is the distance between the two planes
      } = featuresetValues;

      if (!standingMeshBoundingBox) {
        // don't throw an error because it might not be loaded yet
        return null;
      }

      const box = standingMeshBoundingBox;
      const center = box.getCenter(new Vector3());
      const size = box.getSize(new Vector3());

      if (!sagittal_balance_plane) {
        throw new Error(
          'Sagittal balance plane is not defined in sagittal balance plane'
        );
      }

      if (!sagittal_balance) {
        throw new Error(
          'Sagittal balance is not defined in sagittal balance plane'
        );
      }

      if (!coronalMSLPLane) {
        throw new Error(
          'Coronal MSL plane is not defined in sagittal balance plane'
        );
      }

      return {
        sagittal_balance_plane,
        sagittal_balance,
        centerX: center.x,
        centerY: center.y,
        centerZ: center.z,
        sizeX: size.x,
        sizeY: size.y,
        sizeZ: size.z,
        coronalMSLPLane,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [featuresetValues, standingMeshBoundingBox]);

  useEffect(() => {
    if (!result) {
      return;
    }
    addLayerState(layerKey, {
      key: layerKey,
      visible: false,
    });
  }, [addLayerState, result]);

  if (!result) {
    return null;
  }

  const showLayer = isLayerVisible(layerKey);

  return (
    <group name={layerKey} visible={showLayer}>
      <mesh
        position={[
          result.coronalMSLPLane,
          result.centerY,
          result.sagittal_balance_plane,
        ]}
      >
        <boxGeometry args={[result.sizeX * 2, result.sizeY * 2, 0.005]} />
        <meshBasicMaterial color={'blue'} transparent opacity={0.75} />
      </mesh>

      <ThreeDLabel
        plane='sagittal'
        position={[
          result.coronalMSLPLane,
          result.centerY,
          result.sagittal_balance_plane,
        ]}
        label={`${result.sagittal_balance.toFixed(2)}mm`}
        visible={showLayer}
        divPosition={{
          left: 30,
        }}
        layerKey={layerKey}
      />
    </group>
  );
};
